import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, Icon, Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import * as api from '../services/api';

const PageWrapper = styled.div`
  text-align: center;
  padding: 4em 1.5em 3em;
  max-width: 600px;
  margin: 0 auto;
`;

const Logo = styled.img`
  height: 56px;
  margin-bottom: 1.5em;
`;

const Title = styled.h1`
  font-size: 1.8em;
  font-weight: 300;
  color: #333;
  margin: 0 0 0.5em;
`;

const Description = styled.p`
  font-size: 1.1em;
  color: #666;
  line-height: 1.6;
  margin-bottom: 1em;
`;

const CTAButton = styled(Button)`
  &&& {
    border-radius: 20px;
    padding: 0.9em 2.5em;
    font-size: 1.1em;
    box-shadow: 0 2px 8px rgba(33, 133, 208, 0.3);
    margin-top: 1.5em;
  }
`;

const SuccessIcon = styled(Icon)`
  &&& {
    color: #21ba45;
    font-size: 3em;
    margin-bottom: 0.5em;
  }
`;

const ErrorIcon = styled(Icon)`
  &&& {
    color: #db2828;
    font-size: 3em;
    margin-bottom: 0.5em;
  }
`;

const InvitePage = ({ user }) => {
  const { inviteId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);

  const wasAcceptedViaOAuth = searchParams.get('accepted') === 'true';

  useEffect(() => {
    const load = async () => {
      // If redirected back from OAuth with accepted=true, show success
      if (wasAcceptedViaOAuth) {
        setAccepted(true);
        setLoading(false);
        return;
      }

      try {
        const info = await api.getInviteInfo(inviteId);
        setInviteInfo(info);

        // If user is logged in, auto-accept
        if (user) {
          try {
            await api.acceptInvite(inviteId);
            setAccepted(true);
          } catch (err) {
            if (err.response?.status === 410) {
              setError('This invitation has already been used or has expired.');
            } else {
              setError('Failed to accept invitation.');
            }
          }
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError('This invitation was not found.');
        } else if (err.response?.status === 410) {
          setError(err.response.data?.error || 'This invitation has expired or was already used.');
        } else {
          setError('Failed to load invitation.');
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [inviteId, user, wasAcceptedViaOAuth]);

  const handleSignIn = () => {
    window.location.href = api.getLoginUrl('', inviteId);
  };

  if (loading) {
    return (
      <PageWrapper>
        <Loader active>Loading invitation...</Loader>
      </PageWrapper>
    );
  }

  if (accepted) {
    return (
      <PageWrapper>
        <Logo src="/didit_logo_bold.svg" alt="Didit" />
        <SuccessIcon name="check circle" />
        <Title>You're in!</Title>
        <Description>
          You've been added as a delegate. You may now be assigned tasks.
        </Description>
        <CTAButton primary as="a" href="/">
          Go to Didit
        </CTAButton>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <Logo src="/didit_logo_bold.svg" alt="Didit" />
        <ErrorIcon name="times circle" />
        <Title>Invitation Unavailable</Title>
        <Description>{error}</Description>
        <CTAButton primary as="a" href="/">
          Go to Didit
        </CTAButton>
      </PageWrapper>
    );
  }

  // Not logged in, show invite landing page
  return (
    <PageWrapper>
      <Logo src="/didit_logo_bold.svg" alt="Didit" />
      <Title>{inviteInfo?.ownerName} has invited you to be a delegate</Title>
      <Description>
        Didit is a task management app where people can create task lists and delegate items to others.
        As a delegate, you'll be assigned tasks and can track your progress.
      </Description>
      <Description>
        Sign in with Google to accept this invitation.
      </Description>
      <CTAButton primary onClick={handleSignIn}>
        <Icon name="google" /> Sign in with Google
      </CTAButton>
    </PageWrapper>
  );
};

export default InvitePage;
